import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkEmoji from 'remark-emoji';
import { useParams } from 'react-router-dom';
import InfoTechNav from '../InfoTechNav/InfoTechNav';
import ArticlesList from './ArticlesList';
import './MetadataStyles.css';

// Import shared utilities and components
import { detectLanguage, detectLanguageDirection, wrapWithLanguage } from '../../../utils/languageUtils';
import { extractMetadata, ArticleMetadata } from '../../../utils/markdownUtils';
import CodeBlock from '../../Shared/CodeBlock';

const TechnologyNews = () => {
  const { fileName } = useParams();
  const [content, setContent] = useState('');
  const [metadata, setMetadata] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBox, setShowBox] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      if (!fileName) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`/md/TechnologyNews/${fileName}`);
        if (!response.ok) {
          throw new Error('Content not found');
        }
        const text = await response.text();
        
        // Use shared utility for metadata extraction
        const { metadata: parsedMetadata, content: parsedContent } = extractMetadata(text);
        setMetadata(parsedMetadata);
        setContent(parsedContent);
        setError(null);
      } catch (err) {
        console.error('Error fetching markdown:', err);
        setError(err.message || 'Failed to load the article');
        setContent('');
        setMetadata({});
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [fileName]);

  // Define components for rendering markdown with language support
  const components = {
    code: ({ node, inline, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <CodeBlock className={className} {...props}>
          {children}
        </CodeBlock>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    h1: ({ node, children, ...props }) => {
      const lang = detectLanguage(String(children));
      return (
        <h1 {...props} lang={lang} className={`markdown-heading ${lang}`}>
          {wrapWithLanguage(children, lang, true)}
        </h1>
      );
    },
    h2: ({ node, children, ...props }) => {
      const lang = detectLanguage(String(children));
      return (
        <h2 {...props} lang={lang} className={`markdown-heading ${lang}`}>
          {wrapWithLanguage(children, lang, true)}
        </h2>
      );
    },
    h3: ({ node, children, ...props }) => {
      const lang = detectLanguage(String(children));
      return (
        <h3 {...props} lang={lang} className={`markdown-heading ${lang}`}>
          {wrapWithLanguage(children, lang, true)}
        </h3>
      );
    },
    p: ({ node, children, ...props }) => {
      const lang = detectLanguage(String(children));
      return (
        <p {...props} lang={lang} className={`markdown-paragraph ${lang}`}>
          {wrapWithLanguage(children, lang)}
        </p>
      );
    }
  };

  return (
    <div className="it-container">
      {!fileName && showBox && (
        <div className="rtl-box">
          <button className="close-btn" onClick={() => setShowBox(false)}>×</button>
          <p>
            צללו לעולם הטכנולוגיה עם מאמרינו המקיפים והמעניינים.
          </p>
          <p>
            מחומרה ותוכנה ועד טרנדים עדכניים בעולם ההייטק, כאן תמצאו מידע מגוון ועשיר שיעשיר את הידע שלכם ויפתח בפניכם אופקים חדשים בעולם המחשבים המתפתח.
          </p>
        </div>
      )}

      <div className="content-wrapper">
        <InfoTechNav />
        {fileName ? (
          <div className="markdown-content" lang={detectLanguageDirection(content)}>
            {loading && <p>Loading...</p>}
            {error ? (
              <p className="error">{error}</p>
            ) : (
              <>
                <ArticleMetadata metadata={metadata} />
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm, remarkEmoji]}
                  components={components}
                >
                  {content}
                </ReactMarkdown>
              </>
            )}
          </div>
        ) : (
          <ArticlesList 
            folderName="TechnologyNews"
            basePath="information-technology/technology-news"
            defaultCategory="Tech News"
          />
        )}
      </div>
    </div>
  );
};

export default TechnologyNews;